import * as React from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const linkVariants = cva(
  [
    'inline-flex items-center gap-1 rounded-[4px]',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
  ].join(' '),
  {
    variants: {
      intent: {
        // Prose links (WCAG 2.5.8 exempts inline text links from the 44px target size).
        inline: [
          'text-brand-700 no-underline',
          '[text-decoration-line:underline] [text-decoration-thickness:1.5px] [text-underline-offset:3px] [text-decoration-color:rgba(67,56,202,.4)]',
          'hover:text-brand-800 hover:[text-decoration-thickness:2px] hover:[text-decoration-color:currentColor]',
        ].join(' '),
        standalone: [
          'min-h-11 rounded-md px-1 py-2 text-foreground no-underline',
          'hover:bg-brand-500/6 hover:underline',
          // No padding compensation for the border: letting content shift by
          // 3px on activation reads better than the ~1px gap a compensated
          // pl left between the border and the icon.
          'aria-[current=page]:border-l-[3px] aria-[current=page]:border-brand-600 aria-[current=page]:bg-brand-50 aria-[current=page]:font-semibold aria-[current=page]:text-brand-700 aria-[current=page]:hover:no-underline',
          'dark:aria-[current=page]:bg-brand-500/15',
        ].join(' '),
        quiet: 'min-h-11 py-2 text-text-faint no-underline hover:text-foreground hover:underline',
      },
    },
    defaultVariants: { intent: 'standalone' },
  },
);

interface ExternalArrowProps {
  external?: boolean;
}

function ExternalArrow({ external }: ExternalArrowProps) {
  if (!external) return null;
  return <ArrowUpRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
}

export interface LinkProps
  extends Omit<NavLinkProps, 'className' | 'children'>,
    VariantProps<typeof linkVariants> {
  className?: string;
  children: React.ReactNode;
}

/** Internal, router-aware link. Wraps NavLink so `standalone` can style off `aria-current="page"`. */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, intent, children, ...props }, ref) => (
    <NavLink ref={ref} className={cn(linkVariants({ intent }), className)} {...props}>
      {children}
    </NavLink>
  ),
);
Link.displayName = 'Link';

export interface ExternalLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'>,
    VariantProps<typeof linkVariants> {
  className?: string;
  children: React.ReactNode;
}

/** External link: opens in a new tab, carries `rel="noopener noreferrer"`, and shows an arrow glyph. */
export const ExternalLink = React.forwardRef<HTMLAnchorElement, ExternalLinkProps>(
  ({ className, intent, children, target = '_blank', rel = 'noopener noreferrer', ...props }, ref) => (
    <a ref={ref} className={cn(linkVariants({ intent }), className)} target={target} rel={rel} {...props}>
      {children}
      <ExternalArrow external />
    </a>
  ),
);
ExternalLink.displayName = 'ExternalLink';
